import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AttachUserModal = ({ orderId, attachedUserId, onAttachSuccess, token, onUserSelect, isCheckoutMode = false }) => {
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  // Встановлюємо user ID при зміні attachedUserId
  useEffect(() => {
    if (attachedUserId) {
      setUserSearchQuery(`ID: ${attachedUserId}`);
    } else {
      setUserSearchQuery('');
    }
    setUserSearchResults([]);
  }, [attachedUserId]);

  // Перевіряємо стан модалки і встановлюємо значення при відкритті
  useEffect(() => {
    const modalElement = document.getElementById('attachUserModal');
    if (!modalElement) return;

    const handleModalOpen = () => {
      if (attachedUserId) {
        setUserSearchQuery(`ID: ${attachedUserId}`);
      } else {
        setUserSearchQuery('');
      }
      setUserSearchResults([]);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    };

    // Слухаємо подію відкриття модалки
    modalElement.addEventListener('shown.bs.modal', handleModalOpen);

    return () => {
      modalElement.removeEventListener('shown.bs.modal', handleModalOpen);
    };
  }, [attachedUserId]);

  const handleUserSearch = (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    setIsLoading(true);
    // Відновлюємо фокус після встановлення loading стану
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
    
    axios
      .get(`/api/v1/user?idOrPhoneOrEmail=${encodeURIComponent(query)}`, {
        headers: {
          Authorization: token,
        },
      })
      .then(res => {
        if (res.status === 200) {
          setUserSearchResults(res.data.data || []);
        }
        setIsLoading(false);
        // Відновлюємо фокус після отримання результатів
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      })
      .catch(error => {
        setIsLoading(false);
        setUserSearchResults([]);
        // Відновлюємо фокус навіть при помилці
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      });
  };

  // Для імені/прізвища лишаємо пробіли; для чисто цифрового вводу — нормалізація телефону/ID
  const cleanPhoneNumberForSearch = (value) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (/[\p{L}'’`]/u.test(trimmed)) {
      return trimmed.replace(/\s+/g, ' ');
    }

    // Видаляємо всі пробіли, дужки, тире, плюси
    let cleaned = trimmed.replace(/[\s()+\-]/g, '');
    
    // Якщо після очищення це короткий номер (1-4 цифри), не чіпаємо - це може бути ID
    if (cleaned.length <= 4 && /^\d+$/.test(cleaned)) {
      return cleaned;
    }
    
    // Якщо починається з 38 і має рівно 12 цифр (38 + 10 цифр номера), видаляємо 38
    // Це довгий номер телефону
    if (cleaned.startsWith('38') && cleaned.length === 12 && /^38\d{10}$/.test(cleaned)) {
      cleaned = cleaned.substring(2);
    }
    
    return cleaned;
  };

  const handleUserSearchInputChange = (event) => {
    const value = event.target.value;
    const cleanedValue = cleanPhoneNumberForSearch(value);
    setUserSearchQuery(cleanedValue);
    // Затримка для пошуку, щоб не викликати API при кожному символі
    if (cleanedValue.length < 2) {
      setUserSearchResults([]);
      return;
    }
    // Відновлюємо фокус перед викликом API
    if (inputRef.current) {
      inputRef.current.focus();
    }
    handleUserSearch(cleanedValue);
  };

  const formatAttachUserListLine = (user) => {
    if (!user) return '';
    const fullName = [user.surname, user.name].filter((x) => x && String(x).trim()).join(' ').trim();
    const parts = [user.id];
    if (fullName) parts.push(fullName);
    if (user.phoneNumber) parts.push(user.phoneNumber);
    if (user.emailAddress) parts.push(user.emailAddress);
    return parts.join(', ');
  };

  const handleUserSelect = (user) => {
    if (isCheckoutMode) {
      // Режим оформлення замовлення - просто встановлюємо userId без API виклику
      if (onUserSelect) {
        onUserSelect(user.id);
      }
      setUserSearchQuery('');
      setUserSearchResults([]);
      // Закриваємо модалку через клік на кнопку закриття
      const closeButton = document.querySelector('#attachUserModal [data-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
      return;
    }
    
    // Звичайний режим - викликаємо API
    setIsLoading(true);
    
    axios.put(`/api/v1/order/${orderId}/user/${user.id}`, {}, {
      headers: {
        Authorization: token,
      },
    })
      .then(response => {
        setUserSearchQuery('');
        setUserSearchResults([]);
        setIsLoading(false);
        
        // Викликаємо callback після успішного прикріплення та одразу закриваємо модалку
        if (onAttachSuccess) {
          onAttachSuccess();
          document.getElementById('attachUserModal').click(); // Закриваємо модалку одразу
        }
      })
      .catch(error => {
        setIsLoading(false);
        // Можна додати обробку помилки, але без повідомлень в модалці
        console.error('Не вдалося прикріпити користувача:', error);
      });
  };

  return (
    <div className='modal fade' id='attachUserModal' tabIndex='-1' role='dialog'
         aria-labelledby='attachUserModalLabel' aria-hidden='true'>
      <div className='modal-dialog modal-dialog-centered' role='document'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title font-weight-bold'>Прикріпити замовлення до користувача</h5>
            <button type='button' className='close' data-dismiss='modal' aria-label='Close'>
              <span aria-hidden='true'>&times;</span>
            </button>
          </div>
          <div className='modal-body'>
            <div className='form-group'>
              <input
                ref={inputRef}
                type='text'
                className='form-control'
                value={userSearchQuery}
                placeholder='Введіть ID, телефон, імейл або прізвище'
                onChange={handleUserSearchInputChange}
                autoComplete='off'
              />
              {userSearchResults.length > 0 && (
                <ul className='dropdown-menu show'
                    style={{ width: '100%', maxHeight: '150px', overflowY: 'auto' }}>
                  {userSearchResults.map((user, index) => (
                    <React.Fragment key={user.id}>
                      <li className='dropdown-item' onClick={() => handleUserSelect(user)}>
                        {formatAttachUserListLine(user)}
                      </li>
                      {index < userSearchResults.length - 1 && <div className='dropdown-divider'></div>}
                    </React.Fragment>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className='modal-footer'>
            {isCheckoutMode && attachedUserId && (
              <button
                type='button'
                className='btn btn-danger mr-auto'
                onClick={() => {
                  if (onUserSelect) {
                    onUserSelect(null);
                  }
                  setUserSearchQuery('');
                  setUserSearchResults([]);
                  // Закриваємо модалку через клік на кнопку закриття
                  const closeButton = document.querySelector('#attachUserModal [data-dismiss="modal"]');
                  if (closeButton) {
                    closeButton.click();
                  }
                }}
              >
                Відкріпити користувача
              </button>
            )}
            <button type='button' className='btn btn-secondary' data-dismiss='modal'>
              Закрити
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachUserModal;
